import { Root } from "../routes/root"
import { studioCanvasRemountDeps } from "./scenario-studio-routing"
import {
  RootRoute,
  Route,
  Router,
  redirect,
  lazyRouteComponent,
  useParams,
} from "@tanstack/react-router"
import { PresentationPracticeAccess } from "@/components/PresentationPracticeAccess"


// Admin routes

// Journey routes

// Staff-only (Ambr) routes

// Route components are code-split: each loads its own chunk on first visit
// (ENG-1524 — the app previously bundled every route into one 9.6MB chunk).
// Router v1.170 keeps the CURRENT page rendered while a cold chunk loads
// (navigations run in a React transition), so lazy routes never blank the
// app shell — v1.46 suspended cold chunks to the root boundary instead,
// which is why this upgrade accompanied the splitting.
const ReportsLayout = lazyRouteComponent(
  () => import("@/routes/admin/reports"),
  "ReportsLayout"
)
const ReportsOverview = lazyRouteComponent(
  () => import("@/routes/admin/reports"),
  "ReportsOverview"
)
const CatchAll = lazyRouteComponent(() => import("../routes/catch-all"))
const Dashboard = lazyRouteComponent(() => import("../routes/dashboard"), "Dashboard")
const Admin = lazyRouteComponent(() => import("@/routes/admin"), "Admin")
// The Vapi-interview creator pages still serve the public demo routes only —
// the in-app personal creator is the form-based editor below (PSC v2).
const ScenarioCreator = lazyRouteComponent(() => import("@/routes/scenario-creator"))
const ScenarioCreatorCreate = lazyRouteComponent(() => import("@/routes/scenario-creator-interaction"))
const PersonalScenarioCreator = lazyRouteComponent(() => import("@/routes/personal-scenario-creator"))
const CreateScenario = lazyRouteComponent(() => import("@/routes/create-scenario"))
const ScenarioDetail = lazyRouteComponent(() => import("@/routes/scenario-detail"), "ScenarioDetail")
const ScenarioInteractionRoute = lazyRouteComponent(() => import("@/routes/scenario-interaction"), "ScenarioInteractionRoute")
const Call = lazyRouteComponent(() => import("@/routes/call"), "Call")
const Calls = lazyRouteComponent(() => import("@/routes/calls"), "Calls")
const Categories = lazyRouteComponent(() => import("@/routes/categories"), "Categories")
const Category = lazyRouteComponent(() => import("@/routes/category"), "Category")
const MarkdownPreview = lazyRouteComponent(() => import("@/routes/markdown-preview"), "MarkdownPreview")
const UserSettings = lazyRouteComponent(() => import("@/routes/settings"), "Settings")
const Labs = lazyRouteComponent(() => import("@/routes/labs"), "Labs")
const Logout = lazyRouteComponent(() => import("@/routes/logout"), "Logout")
const IndividualUser = lazyRouteComponent(() => import("@/routes/user"), "IndividualUser")
const DemoAvatar = lazyRouteComponent(() => import("@/routes/demo/DemoAvatar"), "DemoAvatar")
const Demo = lazyRouteComponent(() => import("@/routes/demo/Demo"), "Demo")
const DemoFlow = lazyRouteComponent(() => import("@/routes/demo/DemoFlow"), "DemoFlow")
const NetworkCheck = lazyRouteComponent(() => import("@/routes/network-check"), "NetworkCheck")
const LmsCheck = lazyRouteComponent(() => import("@/routes/lms-check"), "LmsCheck")
const LmsLaunch = lazyRouteComponent(() => import("@/routes/lms-launch"), "LmsLaunch")
const MediaInterview = lazyRouteComponent(() => import("@/routes/media-interview"), "MediaInterview")
const MultiSim = lazyRouteComponent(() => import("@/routes/multi-sim"), "MultiSim")
const MultiSimStandalone = lazyRouteComponent(() => import("@/routes/multi-sim"), "MultiSimStandalone")
const DemoKelloggLabs = lazyRouteComponent(() => import("@/routes/demo/DemoKelloggLabs"), "DemoKelloggLabs")
const DemoCustom = lazyRouteComponent(() => import("@/routes/demo/DemoCustom"))
const DemoCustomLimited = lazyRouteComponent(() => import("@/routes/demo/DemoCustom"), "DemoCustomLimited")
const PresentationInteraction = lazyRouteComponent(() => import("@/routes/presentation-interaction"))
const Presentation = lazyRouteComponent(() => import("@/routes/presentation"), "Presentation")
const Home = lazyRouteComponent(() => import("@/components/Home"), "Home")
const Presentations = lazyRouteComponent(() => import("@/routes/presentations"), "Presentations")
const PresentationNew = lazyRouteComponent(() => import("@/routes/presentation-new"))
const PresentationTypes = lazyRouteComponent(() => import("@/routes/presentation-types"), "PresentationTypes")
const CoachingNew = lazyRouteComponent(() => import("@/routes/coaching-new"))
const Coaching = lazyRouteComponent(() => import("@/routes/coaching"), "Coaching")
const WorkosCallback = lazyRouteComponent(() => import("@/routes/workos-callback"), "WorkosCallback")
const Track = lazyRouteComponent(() => import("@/routes/track"), "Track")
const Referrals = lazyRouteComponent(() => import("@/routes/referrals"), "Referrals")
const ReferralTerms = lazyRouteComponent(() => import("@/routes/referral-terms"), "ReferralTerms")
const UserManagementTab = lazyRouteComponent(() => import("@/routes/admin/users"))
const TeamPermissionsTab = lazyRouteComponent(() => import("@/routes/admin/team-permissions"))
const TeamNew = lazyRouteComponent(() => import("@/routes/admin/team-new"))
const TeamDetailPage = lazyRouteComponent(() => import("@/routes/admin/team-detail"))
const DashboardTestPage = lazyRouteComponent(() => import("@/routes/dashboard/test"), "DashboardTestPage")
const GoogleVivatech = lazyRouteComponent(() => import("@/routes/google-vivatech"), "GoogleVivatech")
const ShadcnPreview = lazyRouteComponent(() => import("@/routes/shadcn-preview"), "ShadcnPreview")
const Modules = lazyRouteComponent(() => import("@/routes/admin/modules"))
const Streaks = lazyRouteComponent(() => import("@/routes/admin/streaks"))
const Slack = lazyRouteComponent(() => import("@/routes/admin/slack"))
const AdminSettings = lazyRouteComponent(() => import("@/routes/admin/settings"))
const AdminLms = lazyRouteComponent(() => import("@/routes/admin/lms"), "AdminLms")
const ScenarioStudio = lazyRouteComponent(() => import("@/routes/scenario-studio"))
const ScenarioStudioCreate = lazyRouteComponent(() => import("@/routes/scenario-studio-create"))
const Journeys = lazyRouteComponent(() => import("@/routes/journeys"), "Journeys")
const JourneyDetail = lazyRouteComponent(() => import("@/routes/journey-detail"), "JourneyDetail")
const AdminJourneys = lazyRouteComponent(() => import("@/routes/admin/journeys"))
const AdminJourneyDetail = lazyRouteComponent(() => import("@/routes/admin/journey-detail"))
const JourneyForm = lazyRouteComponent(() => import("@/routes/admin/journey-form"))
const ReportsIndividuals = lazyRouteComponent(() => import("@/routes/admin/reports/individuals"))
const ReportsCompletion = lazyRouteComponent(() => import("@/routes/admin/reports/completion"))
const ReportsJourneys = lazyRouteComponent(() => import("@/routes/admin/reports/journeys"))
const Staff = lazyRouteComponent(() => import("@/routes/staff"), "Staff")
const StaffIndexRedirect = lazyRouteComponent(() => import("@/routes/staff"), "StaffIndexRedirect")
const StaffReviewQueue = lazyRouteComponent(() => import("@/routes/staff/review-queue"))
const StaffFeedbackFrameworkEditor = lazyRouteComponent(() => import("@/routes/staff/feedback-framework-editor"))
const StaffEarlyAccess = lazyRouteComponent(() => import("@/routes/staff/early-access"))
const StaffContent = lazyRouteComponent(() => import("@/routes/staff/content"))
const StaffVerifyMigration = lazyRouteComponent(() => import("@/routes/staff/verify-migration"))
const StaffContentEditor = lazyRouteComponent(() => import("@/routes/staff/content-editor"))
const McpAuthorize = lazyRouteComponent(() => import("@/routes/mcp-authorize"), "McpAuthorize")

export const localStorageTokenKey = "token"

const rootRoute = new RootRoute({
  component: Root,
})

const catchRoute = new Route({
  staticData: { title: "Not found" },
  getParentRoute: () => rootRoute,
  path: "*",
  component: CatchAll,
})

const workosCallbackRoute = new Route({
  getParentRoute: () => rootRoute,
  path: "/workos/callback",
  component: WorkosCallback,
})

// START DASHBOARD PAGES ---------------------

const dashboardRoute = new Route({
  getParentRoute: () => rootRoute,
  id: "dashboard",
  component: Dashboard,
})

export const indexRoute = new Route({
  staticData: { title: "Home" },
  getParentRoute: () => dashboardRoute,
  path: "/",
  component: Home,
})

// Dedicated SSO login route renders the same Home component.
// When unauthenticated, Dashboard will render <Login /> which
// detects this path to switch to SSO-only mode.
const loginSsoRoute = new Route({
  staticData: { title: "Login" },
  getParentRoute: () => dashboardRoute,
  path: "/login/sso",
  component: Home,
})

// Dedicated single-option login pages — same Home component, Login detects the
// path to render only one auth method. Shareable with orgs that want a single option.
const loginMicrosoftRoute = new Route({
  staticData: { title: "Login" },
  getParentRoute: () => dashboardRoute,
  path: "/login/microsoft",
  component: Home,
})

const loginGoogleRoute = new Route({
  staticData: { title: "Login" },
  getParentRoute: () => dashboardRoute,
  path: "/login/google",
  component: Home,
})

const loginMagicLinkRoute = new Route({
  staticData: { title: "Login" },
  getParentRoute: () => dashboardRoute,
  path: "/login/magic-link",
  component: Home,
})

export const categoriesRoute = new Route({
  staticData: { title: "Scenarios" },
  getParentRoute: () => dashboardRoute,
  path: "/categories",
  component: Categories,
})

const categoryRoute = new Route({
  staticData: { title: "Scenarios" },
  getParentRoute: () => dashboardRoute,
  path: "/category/$categoryId",
  component: Category,
})

const scenarioDetailRoute = new Route({
  staticData: { title: "Scenario" },
  getParentRoute: () => dashboardRoute,
  path: "/scenario/$scenarioId/detail",
  component: ScenarioDetail,
})

const scenarioInteractionRoute = new Route({
  staticData: { title: "Simulation" },
  getParentRoute: () => dashboardRoute,
  path: "/scenario/$scenarioId/interaction",
  component: ScenarioInteractionRoute,
})

// Personal Scenario Creator (PSC v2): form → generate → edit → practice, on
// the Studio stack. Replaces the in-app Vapi-interview flow; the public demo
// routes below run the same pipeline through a short logged-out form.
// ?draft reopens an existing personal scenario; ?autostart is the home-page
// "prepare for a conversation" entry point. The situation text itself is
// handed over in memory (useTempStore.pendingPersonalScenarioSituation) and
// is deliberately NOT a search param: a description of someone's real
// conversation must never land in the address bar, history, copied links or
// logs.
const scenarioCreatorRoute = new Route({
  staticData: { title: "Scenario creator" },
  getParentRoute: () => dashboardRoute,
  path: "/scenario-creator",
  component: PersonalScenarioCreator,
  // Optional-props annotation (same idiom as the demo creator routes) so
  // links and navigations may omit any of them.
  validateSearch: (search): { draft?: string; autostart?: boolean } => {
    return {
      draft: search.draft != null ? String(search.draft) : undefined,
      autostart: search.autostart ? true : undefined,
    }
  },
})

// The old in-app interview page is retired — bookmarks land on the editor.
// The mode search param is kept in the schema (harmlessly dropped by the
// redirect) so links written for the old page still typecheck.
const scenarioCreatorCreateRoute = new Route({
  staticData: { title: "Scenario creator" },
  getParentRoute: () => dashboardRoute,
  path: "/scenario-creator/create",
  validateSearch: (search): { mode?: "voice" | "text" } => {
    return {
      mode: (search.mode as "voice" | "text") || undefined,
    }
  },
  beforeLoad: () => {
    throw redirect({ to: "/scenario-creator", search: {} })
  },
})

// New unified entry point for scenario creation
const createScenarioRoute = new Route({
  staticData: { title: "Create scenario" },
  getParentRoute: () => dashboardRoute,
  path: "/scenarios/new",
  component: CreateScenario,
})

// The old Org Scenario Creator wizard is retired — the Scenario Studio
// canvas replaces it. Old links (bookmarks, customer emails) redirect rather
// than 404; the canvas itself gates non-admins.
const orgScenarioCreatorRoute = new Route({
  staticData: { title: "Scenario creator" },
  getParentRoute: () => dashboardRoute,
  path: "/org-scenario-creator",
  beforeLoad: () => {
    throw redirect({ to: "/scenario-studio/create" })
  },
})

// Scenario Studio: org-admin workspace for custom scenarios (lifecycle view)
const scenarioStudioRoute = new Route({
  staticData: { title: "Scenario Studio" },
  getParentRoute: () => dashboardRoute,
  path: "/scenario-studio",
  component: ScenarioStudio,
})

// Scenario Studio creation canvas — blank canvas and reopen-a-draft variants
// share one component (it reads the optional $scenarioId param).
const scenarioStudioCreateRoute = new Route({
  staticData: { title: "Create scenario" },
  getParentRoute: () => dashboardRoute,
  path: "/scenario-studio/create",
  component: ScenarioStudioCreate,
  remountDeps: studioCanvasRemountDeps,
})

const scenarioStudioDraftRoute = new Route({
  staticData: { title: "Edit draft" },
  getParentRoute: () => dashboardRoute,
  path: "/scenario-studio/draft/$scenarioId",
  component: ScenarioStudioCreate,
  remountDeps: studioCanvasRemountDeps,
})

const callsRoute = new Route({
  staticData: { title: "History" },
  getParentRoute: () => dashboardRoute,
  path: "/conversations",
  component: Calls,
})

const presentationsRoute = new Route({
  staticData: { title: "Presentations" },
  getParentRoute: () => dashboardRoute,
  path: "/presentations",
  component: Presentations,
})

const coachingRoute = new Route({
  staticData: { title: "Coaching" },
  getParentRoute: () => dashboardRoute,
  path: "/coaching",
  component: Coaching,
})

const callRoute = new Route({
  staticData: { title: "Conversation" },
  getParentRoute: () => dashboardRoute,
  path: "/calls/$callId",
  component: Call,
})

const adminRoute = new Route({
  getParentRoute: () => dashboardRoute,
  path: "/admin",
  component: Admin,
})

// "Reporting and Insights" — the analytics engine folded into the admin
// dashboard. This pathless layout owns the secondary sub-tab bar + filter bar
// (ReportsLayout); its children render below it inside the admin shell.
const adminReportingRoute = new Route({
  getParentRoute: () => adminRoute,
  id: "adminReporting",
  component: ReportsLayout,
})

const adminIndexRoute = new Route({
  staticData: { title: "Analytics" },
  getParentRoute: () => adminReportingRoute,
  path: "/",
  component: ReportsOverview,
})

const adminIndividualsRoute = new Route({
  staticData: { title: "Individuals" },
  getParentRoute: () => adminReportingRoute,
  path: "/analytics/individuals",
  component: ReportsIndividuals,
})

const adminCompletionRoute = new Route({
  staticData: { title: "Completion tracking" },
  getParentRoute: () => adminReportingRoute,
  path: "/analytics/completion",
  component: ReportsCompletion,
})

// Read-only journey reporting sub-tab. The /analytics prefix keeps it clear
// of the top-level /admin/journeys management page.
const adminJourneysReportRoute = new Route({
  staticData: { title: "Journeys reporting" },
  getParentRoute: () => adminReportingRoute,
  path: "/analytics/journeys",
  component: ReportsJourneys,
})

// Previous analytics slugs (pre-/analytics prefix) — thin redirects
const adminIndividualsLegacyRoute = new Route({
  getParentRoute: () => adminRoute,
  path: "/individuals",
  beforeLoad: () => {
    throw redirect({ to: "/admin/analytics/individuals" })
  },
})

const adminCompletionLegacyRoute = new Route({
  getParentRoute: () => adminRoute,
  path: "/completion",
  beforeLoad: () => {
    throw redirect({ to: "/admin/analytics/completion" })
  },
})

const adminJourneysReportLegacyRoute = new Route({
  getParentRoute: () => adminRoute,
  path: "/journeys-report",
  beforeLoad: () => {
    throw redirect({ to: "/admin/analytics/journeys" })
  },
})

// Legacy Usage Analytics page — its successor is the Individuals reporting
// tab, so keep old bookmarks/shared links working with a thin redirect
// (mirrors the /reports/* redirects below).
const adminUsageLegacyRoute = new Route({
  getParentRoute: () => adminRoute,
  path: "/usage",
  beforeLoad: () => {
    throw redirect({ to: "/admin/analytics/individuals" })
  },
})

const adminUserManagementRoute = new Route({
  staticData: { title: "Users" },
  getParentRoute: () => adminRoute,
  path: "/users",
  component: UserManagementTab,
})

// New canonical route for Groups (formerly Team Permissions)
const adminTeamsRoute = new Route({
  staticData: { title: "Teams" },
  getParentRoute: () => adminRoute,
  path: "/teams",
  component: TeamPermissionsTab,
})

// Group creation wizard
const adminTeamNewRoute = new Route({
  staticData: { title: "New team" },
  getParentRoute: () => adminRoute,
  path: "/teams/new",
  component: TeamNew,
})

// Group detail/edit page
const adminTeamDetailRoute = new Route({
  staticData: { title: "Team" },
  getParentRoute: () => adminRoute,
  path: "/teams/$teamId",
  component: TeamDetailPage,
})

// Legacy path retained for deep-link compatibility
const adminTeamPermissionsRoute = new Route({
  staticData: { title: "Teams" },
  getParentRoute: () => adminRoute,
  path: "/team-permissions",
  component: TeamPermissionsTab,
})

// Legacy alias to preserve deep links that may have pointed under /admin/users
const adminTeamPermissionsLegacyRoute = new Route({
  staticData: { title: "Teams" },
  getParentRoute: () => adminRoute,
  path: "/users/team-permissions",
  component: TeamPermissionsTab,
})

const modulesSettingsRoute = new Route({
  staticData: { title: "Modules" },
  getParentRoute: () => adminRoute,
  path: "/modules",
  component: Modules,
})

const streaksSettingsRoute = new Route({
  staticData: { title: "Streaks" },
  getParentRoute: () => adminRoute,
  path: "/streaks",
  component: Streaks,
})

const slackSettingsRoute = new Route({
  staticData: { title: "Slack" },
  getParentRoute: () => adminRoute,
  path: "/slack",
  component: Slack,
})

// ENG-1474: LMS (SCORM) delivery for entitled orgs
const adminLmsRoute = new Route({
  staticData: { title: "LMS" },
  getParentRoute: () => adminRoute,
  path: "/lms",
  component: AdminLms,
})

const adminSettingsRoute = new Route({
  staticData: { title: "Org settings" },
  getParentRoute: () => adminRoute,
  path: "/settings",
  component: AdminSettings,
})

// Admin journey routes
const adminJourneysRoute = new Route({
  staticData: { title: "Journeys" },
  getParentRoute: () => adminRoute,
  path: "/journeys",
  component: AdminJourneys,
})

const adminJourneyDetailRoute = new Route({
  staticData: { title: "Journey" },
  getParentRoute: () => adminRoute,
  path: "/journeys/$journeyId",
  component: AdminJourneyDetail,
})

const adminJourneyNewRoute = new Route({
  staticData: { title: "New journey" },
  getParentRoute: () => adminRoute,
  path: "/journeys/new",
  component: JourneyForm,
})

const adminJourneyEditRoute = new Route({
  staticData: { title: "Edit journey" },
  getParentRoute: () => adminRoute,
  path: "/journeys/$journeyId/edit",
  component: JourneyForm,
})

// Consent screen for the staff MCP connector (ENG-1138). Under dashboardRoute
// so a signed-out visitor gets the normal login in place, URL intact.
const mcpAuthorizeRoute = new Route({
  staticData: { title: "Connect Claude" },
  getParentRoute: () => dashboardRoute,
  path: "/mcp/authorize",
  component: McpAuthorize,
})

// Staff-only routes — the Staff layout gates on isUserPrivileged
const staffRoute = new Route({
  getParentRoute: () => dashboardRoute,
  path: "/staff",
  component: Staff,
})

const staffIndexRoute = new Route({
  getParentRoute: () => staffRoute,
  path: "/",
  component: StaffIndexRedirect,
})

// First staff tab. A literal segment, so it wins over /staff/$entity.
const staffReviewQueueRoute = new Route({
  staticData: { title: "Review queue" },
  getParentRoute: () => staffRoute,
  path: "/review-queue",
  component: StaffReviewQueue,
})

// Feedback frameworks are the `promptBuilders` entity: the generic table for
// the list, the bespoke editor (below) for a record.
const staffFeedbackFrameworksRoute = new Route({
  staticData: { title: "Feedback frameworks" },
  getParentRoute: () => staffRoute,
  path: "/feedback-frameworks",
  component: StaffContent,
  validateSearch: (search): Record<string, unknown> => search,
})

const staffFeedbackFrameworkEditorRoute = new Route({
  staticData: { title: "Feedback framework" },
  getParentRoute: () => staffRoute,
  path: "/feedback-frameworks/$frameworkId",
  component: StaffFeedbackFrameworkEditor,
})

// ENG-1138: `promptBuilders` (feedback frameworks) uses the generic table like
// every other entity — status, org, call type and usage are computed columns
// projected out of sourceFields, so it inherits pagination, the column picker,
// multi-select, URL filters and export/import.
//
// Only the RECORD view stays bespoke, and for a reason the generic form cannot
// meet: it is a structured criteria and tone builder with a live
// compiled-prompt preview, a publish gated on that preview passing QA, and a
// test bench that runs the framework against a real past call. A flat field
// list would lose all of it.
function StaffContentRecord() {
  const { entity } = useParams({ strict: false }) as { entity?: string }
  if (entity === "promptBuilders") {
    return <StaffFeedbackFrameworkEditor />
  } else {
    return <StaffContentEditor />
  }
}

// ENG-1138: proves the cutover by diffing the CMS against the database.
const staffVerifyMigrationRoute = new Route({
  staticData: { title: "Verify migration" },
  getParentRoute: () => staffRoute,
  path: "/verify-migration",
  component: StaffVerifyMigration,
})

// Scenario types and skills sit under Scenarios, and each of Scenarios and
// Categories has a Core view (the same table, filtered to the core flag).
// Literal segments, so they win over /staff/$entity/$recordId.
const staffScenarioTypesRoute = new Route({
  staticData: { title: "Scenario types" },
  getParentRoute: () => staffRoute,
  path: "/scenarios/types",
  component: StaffContent,
  validateSearch: (search): Record<string, unknown> => search,
})
const staffScenarioTypeRecordRoute = new Route({
  staticData: { title: "Scenario type" },
  getParentRoute: () => staffRoute,
  path: "/scenarios/types/$recordId",
  component: StaffContentEditor,
})
const staffSkillsRoute = new Route({
  staticData: { title: "Skills" },
  getParentRoute: () => staffRoute,
  path: "/scenarios/skills",
  component: StaffContent,
  validateSearch: (search): Record<string, unknown> => search,
})
const staffSkillRecordRoute = new Route({
  staticData: { title: "Skill" },
  getParentRoute: () => staffRoute,
  path: "/scenarios/skills/$recordId",
  component: StaffContentEditor,
})
const staffCoreScenariosRoute = new Route({
  staticData: { title: "Core scenarios" },
  getParentRoute: () => staffRoute,
  path: "/scenarios/core",
  component: StaffContent,
  validateSearch: (search): Record<string, unknown> => search,
})
const staffCoreCategoriesRoute = new Route({
  staticData: { title: "Core categories" },
  getParentRoute: () => staffRoute,
  path: "/categories/core",
  component: StaffContent,
  validateSearch: (search): Record<string, unknown> => search,
})

// Every other entity. Scenario types and skills have their own routes above
// (link to them with entityListLink / entityRecordLink).
const staffContentListRoute = new Route({
  staticData: { title: "Content" },
  getParentRoute: () => staffRoute,
  path: "/$entity",
  component: StaffContent,
  // The generic table keeps its state in the URL (q/sort/dir/cols/page/size),
  // and the promptBuilders library keeps its filters there too. Passing them
  // straight through means neither has to be enumerated here.
  validateSearch: (search): Record<string, unknown> => search,
})

// recordId "new" opens the create form.
const staffContentEditorRoute = new Route({
  staticData: { title: "Content" },
  getParentRoute: () => staffRoute,
  path: "/$entity/$recordId",
  component: StaffContentRecord,
})

// Early access registry editor: manage the Labs page cards without a deploy.
const staffEarlyAccessRoute = new Route({
  staticData: { title: "Early access" },
  getParentRoute: () => staffRoute,
  path: "/early-access",
  component: StaffEarlyAccess,
})

// Legacy /reports/* paths now live under the admin dashboard ("Reporting and
// Insights"). Keep thin redirect routes so old links/bookmarks still resolve.
// (Route with no component renders a default <Outlet/>.)
const reportsRoute = new Route({
  getParentRoute: () => dashboardRoute,
  path: "/reports",
})

const reportsIndexRoute = new Route({
  getParentRoute: () => reportsRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/admin" })
  },
})

const reportsIndividualsRoute = new Route({
  getParentRoute: () => reportsRoute,
  path: "/individuals",
  beforeLoad: () => {
    throw redirect({ to: "/admin/analytics/individuals" })
  },
})

const reportsCompletionRoute = new Route({
  getParentRoute: () => reportsRoute,
  path: "/completion",
  beforeLoad: () => {
    throw redirect({ to: "/admin/analytics/completion" })
  },
})

const reportsJourneysRoute = new Route({
  getParentRoute: () => reportsRoute,
  path: "/journeys",
  beforeLoad: () => {
    throw redirect({ to: "/admin/analytics/journeys" })
  },
})

const markdownPreview = new Route({
  staticData: { title: "Markdown preview" },
  getParentRoute: () => dashboardRoute,
  path: "/markdown-preview",
  component: MarkdownPreview,
})

const settings = new Route({
  staticData: { title: "Settings" },
  getParentRoute: () => dashboardRoute,
  path: "/settings",
  component: UserSettings,
})

const labs = new Route({
  staticData: { title: "Labs" },
  getParentRoute: () => dashboardRoute,
  path: "/labs",
  component: Labs,
})

const presentationNew = new Route({
  staticData: { title: "Start practice" },
  getParentRoute: () => dashboardRoute,
  path: "/presentations/new",
  component: () => (
    <PresentationPracticeAccess>
      <PresentationNew />
    </PresentationPracticeAccess>
  ),
})

const coachingNew = new Route({
  staticData: { title: "Start a coaching session" },
  getParentRoute: () => dashboardRoute,
  path: "/coaching/new",
  component: CoachingNew,
})

const presentationTypes = new Route({
  staticData: { title: "Presentation types" },
  getParentRoute: () => dashboardRoute,
  path: "/presentations/types",
  component: () => (
    <PresentationPracticeAccess>
      <PresentationTypes />
    </PresentationPracticeAccess>
  ),
})

const presentationInteraction = new Route({
  staticData: { title: "Presentation" },
  getParentRoute: () => dashboardRoute,
  path: "/presentations/interaction",
  component: () => (
    <PresentationPracticeAccess>
      <PresentationInteraction />
    </PresentationPracticeAccess>
  ),
  validateSearch: (search) => {
    return {
      type: search.type as string,
    }
  },
})

const presentation = new Route({
  staticData: { title: "Presentation" },
  getParentRoute: () => dashboardRoute,
  path: "/presentations/$presentationId",
  component: Presentation,
})

const logout = new Route({
  staticData: { title: "Logout" },
  getParentRoute: () => dashboardRoute,
  path: "/logout",
  component: Logout,
})

const individualUser = new Route({
  staticData: { title: "User" },
  getParentRoute: () => dashboardRoute,
  path: "/admin/users/$userId",
  component: IndividualUser,
})

const dashboardTestRoute = new Route({
  staticData: { title: "Test" },
  getParentRoute: () => dashboardRoute,
  path: "/test",
  component: DashboardTestPage,
})

const trackRoute = new Route({
  staticData: { title: "Track" },
  getParentRoute: () => dashboardRoute,
  path: "/track",
  component: Track,
})

// User journey routes
const journeysRoute = new Route({
  staticData: { title: "Journeys" },
  getParentRoute: () => dashboardRoute,
  path: "/journeys",
  component: Journeys,
})

const journeyDetailRoute = new Route({
  staticData: { title: "Journey" },
  getParentRoute: () => dashboardRoute,
  path: "/journeys/$journeyId",
  component: JourneyDetail,
})

const referralsRoute = new Route({
  staticData: { title: "Referrals" },
  getParentRoute: () => dashboardRoute,
  path: "/referrals",
  component: Referrals,
})

const referralTermsRoute = new Route({
  staticData: { title: "Referral terms" },
  getParentRoute: () => dashboardRoute,
  path: "/referral-terms",
  component: ReferralTerms,
})

// END DASHBOARD PAGES -----------------------

// START DEMO PAGES ---------------------

const demoRoute = new Route({
  staticData: { title: "Demo" },
  getParentRoute: () => rootRoute,
  path: "/demo",
  component: Demo,
  validateSearch: (
    search,
  ): {
    fromDemoFlow?: string
    step?: string
    scenarioId?: string | number
    callId?: string
    page?: string
  } => {
    return {
      fromDemoFlow: (search.fromDemoFlow as string) || undefined,
      step: (search.step as string) || undefined,
      scenarioId: search.scenarioId as string | number | undefined,
      callId: (search.callId as string) || undefined,
      page: (search.page as string) || undefined,
    }
  },
})

const demoWithOrgRoute = new Route({
  staticData: { title: "Demo" },
  getParentRoute: () => rootRoute,
  path: "/demo/$demoOrgId",
  component: Demo,
  validateSearch: (
    search,
  ): {
    fromDemoFlow?: string
    step?: string
    scenarioId?: string | number
    callId?: string
    page?: string
  } => {
    return {
      // Pass through fromDemoFlow for child routes
      fromDemoFlow: (search.fromDemoFlow as string) || undefined,
      // Pass through other common demo search params
      step: (search.step as string) || undefined,
      // scenarioId can be number or string depending on how it was set
      scenarioId: search.scenarioId as string | number | undefined,
      callId: (search.callId as string) || undefined,
      page: (search.page as string) || undefined,
    }
  },
})

const demoAvatarRoute = new Route({
  getParentRoute: () => demoRoute,
  path: "/avatar",
  component: DemoAvatar,
})

const demoIndexRoute = new Route({
  getParentRoute: () => demoRoute,
  path: "/",
  component: DemoFlow,
})

const demoIndexWithOrgRoute = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/",
  component: DemoFlow,
})

const demoScenarioDetailRoute = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/scenario/$scenarioId/detail",
  component: ScenarioDetail,
})

const demoScenarioInteractionRoute = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/scenario/$scenarioId/interaction",
  component: ScenarioInteractionRoute,
})

const demoCallRoute = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/calls/$callId",
  component: Call,
  validateSearch: (search) => {
    return {
      fromDemoFlow: (search.fromDemoFlow as string) || undefined,
    }
  },
})

const demoPresentationNew = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/presentations/new",
  component: PresentationNew,
})

const demoPresentationTypes = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/presentations/types",
  component: PresentationTypes,
})

const demoPresentationInteraction = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/presentations/interaction",
  component: PresentationInteraction,
  validateSearch: (search) => {
    return {
      type: search.type as string,
    }
  },
})

const demoPresentation = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/presentations/$presentationId",
  component: Presentation,
})

const demoScenarioCreatorRoute = new Route({
  getParentRoute: () => demoRoute,
  path: "/scenario-creator",
  component: ScenarioCreator,
})

const demoScenarioCreatorCreateRoute = new Route({
  getParentRoute: () => demoRoute,
  path: "/scenario-creator/create",
  component: ScenarioCreatorCreate,
  validateSearch: (search) => {
    return {
      mode: (search.mode as "voice" | "text") || undefined,
    }
  },
})

const demoScenarioCreatorRouteWithOrg = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/scenario-creator",
  component: ScenarioCreator,
})

const demoScenarioCreatorCreateRouteWithOrg = new Route({
  getParentRoute: () => demoWithOrgRoute,
  path: "/scenario-creator/create",
  component: ScenarioCreatorCreate,
  validateSearch: (search): { mode?: "voice" | "text" } => {
    return {
      mode: (search.mode as "voice" | "text") || undefined,
    }
  },
})

const googleVivatechRoute = new Route({
  staticData: { title: "Google VivaTech" },
  getParentRoute: () => rootRoute,
  path: "/google-vivatech",
  component: GoogleVivatech,
})

// Internal gallery of shadcn components on the design system — dev reference
const shadcnPreviewRoute = new Route({
  staticData: { title: "Shadcn preview" },
  getParentRoute: () => rootRoute,
  path: "/shadcn-preview",
  component: ShadcnPreview,
})

const demoKelloggLabsRoute = new Route({
  staticData: { title: "Kellogg Labs" },
  getParentRoute: () => demoRoute,
  path: "/kellogg-labs/",
  component: DemoKelloggLabs,
})

const demoCustomRoute = new Route({
  getParentRoute: () => demoRoute,
  path: "/custom",
  component: DemoCustom,
})

const demoCustomLimitedRoute = new Route({
  getParentRoute: () => demoRoute,
  path: "/custom/limited",
  component: DemoCustomLimited,
})

// END DEMO PAGES -----------------------

// Voice network check — public route, no auth. Used by trial users, buyers
// and customer IT teams to verify their corporate network allows Ambr's
// real-time voice connections (and to produce a whitelisting report if not).
// ENG-1476: the LMS launch window a SCORM package opens. A root child, outside
// the dashboard shell: no sidebar, onboarding, identify calls or normal login;
// it runs on a session scoped to one roleplay attempt.
const lmsLaunchRoute = new Route({
  staticData: { title: "Roleplay" },
  getParentRoute: () => rootRoute,
  path: "/lms/launch",
  component: LmsLaunch,
})

// ENG-1472: the public LMS check (probe) download — public route, no auth,
// so a customer can be sent a link rather than an email attachment
const lmsCheckRoute = new Route({
  staticData: { title: "LMS check" },
  getParentRoute: () => rootRoute,
  path: "/lms-check",
  component: LmsCheck,
})

const networkCheckRoute = new Route({
  staticData: { title: "Network check" },
  getParentRoute: () => rootRoute,
  path: "/network-check",
  component: NetworkCheck,
  // `source`/`provider`/`reason`/`ref` are set when the check is launched
  // from a failed call, so the run records what prompted it; `auto` starts
  // the probes without a second click.
  validateSearch: (
    search,
  ): {
    id?: string
    source?: string
    provider?: string
    reason?: string
    ref?: string
    auto?: string
    email?: string
    org?: string
  } => ({
    id: (search.id as string) || undefined,
    source: (search.source as string) || undefined,
    provider: (search.provider as string) || undefined,
    reason: (search.reason as string) || undefined,
    ref: (search.ref as string) || undefined,
    auto: (search.auto as string) || undefined,
    // Prefill carried from the failing call, because this route never loads
    // auth and the user store is empty here even when the visitor is signed in
    email: (search.email as string) || undefined,
    org: (search.org as string) || undefined,
  }),
})

// Media interview simulator — public route, no auth, separate flow from /demo
const mediaInterviewRoute = new Route({
  staticData: { title: "Media interview" },
  getParentRoute: () => rootRoute,
  path: "/media-interview",
  component: MediaInterview,
  validateSearch: (search): { step?: string } => ({
    step: (search.step as string) || undefined,
  }),
})

// Group simulations beta — authenticated dashboard route, additionally gated
// by the multi-participant-sims PostHog flag inside the component
const groupSimsRoute = new Route({
  staticData: { title: "Group simulations" },
  getParentRoute: () => dashboardRoute,
  path: "/conversations/group-sims",
  component: MultiSim,
})

// Dev/test-only standalone variant (no auth, own page chrome) so the feature
// can be exercised outside the dashboard shell — never registered in prod
const multiSimDevRoute = new Route({
  staticData: { title: "Group simulations" },
  getParentRoute: () => rootRoute,
  path: "/multi-sim",
  component: MultiSimStandalone,
})

const routeTree = rootRoute.addChildren([
  dashboardRoute.addChildren([
    mcpAuthorizeRoute,
    scenarioDetailRoute,
    scenarioInteractionRoute,
    scenarioCreatorRoute,
    scenarioCreatorCreateRoute,
    createScenarioRoute,
    orgScenarioCreatorRoute,
    scenarioStudioRoute,
    scenarioStudioCreateRoute,
    scenarioStudioDraftRoute,
    callRoute,
    adminRoute.addChildren([
      // "Reporting and Insights" layout + its sub-tabs (Overview / Individuals
      // / Completion / Journeys reporting) render inside the admin shell.
      adminReportingRoute.addChildren([
        adminIndexRoute,
        adminIndividualsRoute,
        adminCompletionRoute,
        adminJourneysReportRoute,
      ]),
      adminUsageLegacyRoute,
      adminIndividualsLegacyRoute,
      adminCompletionLegacyRoute,
      adminJourneysReportLegacyRoute,
      adminUserManagementRoute,
      adminTeamNewRoute,
      adminTeamDetailRoute,
      adminTeamsRoute,
      // Legacy aliases retained until we confirm removal
      adminTeamPermissionsRoute,
      adminTeamPermissionsLegacyRoute,
      modulesSettingsRoute,
      streaksSettingsRoute,
      slackSettingsRoute,
      adminSettingsRoute,
      adminLmsRoute,
      // Journey admin routes
      adminJourneysRoute,
      adminJourneyNewRoute,
      adminJourneyDetailRoute,
      adminJourneyEditRoute,
    ]),
    staffRoute.addChildren([
      staffIndexRoute,
      staffReviewQueueRoute,
      staffFeedbackFrameworksRoute,
      staffFeedbackFrameworkEditorRoute,
      staffVerifyMigrationRoute,
      staffScenarioTypesRoute,
      staffScenarioTypeRecordRoute,
      staffSkillsRoute,
      staffSkillRecordRoute,
      staffCoreScenariosRoute,
      staffCoreCategoriesRoute,
      staffContentListRoute,
      staffContentEditorRoute,
      staffEarlyAccessRoute,
    ]),
    callsRoute,
    categoryRoute,
    markdownPreview,
    settings,
    labs,
    presentation,
    logout,
    individualUser,
    indexRoute,
    loginSsoRoute,
    loginMicrosoftRoute,
    loginGoogleRoute,
    loginMagicLinkRoute,
    categoriesRoute,
    presentationsRoute,
    presentationNew,
    presentationTypes,
    presentationInteraction,
    coachingNew,
    coachingRoute,
    dashboardTestRoute,
    trackRoute,
    // User journey routes
    journeysRoute,
    journeyDetailRoute,
    referralsRoute,
    referralTermsRoute,
    // Reports section — Ambr Analytics Engine (admin-only via main nav)
    reportsRoute.addChildren([
      reportsIndexRoute,
      reportsIndividualsRoute,
      reportsCompletionRoute,
      reportsJourneysRoute,
    ]),
    groupSimsRoute,
  ]),
  demoRoute.addChildren([
    demoAvatarRoute,
    demoIndexRoute,
    demoScenarioCreatorRoute,
    demoScenarioCreatorCreateRoute,
    demoCustomRoute,
    demoCustomLimitedRoute,
  ]),
  demoWithOrgRoute.addChildren([
    demoIndexWithOrgRoute,
    demoCallRoute,
    demoScenarioDetailRoute,
    demoScenarioInteractionRoute,
    demoPresentationNew,
    demoPresentationTypes,
    demoPresentationInteraction,
    demoPresentation,
    demoScenarioCreatorRouteWithOrg,
    demoScenarioCreatorCreateRouteWithOrg,
  ]),
  catchRoute,
  workosCallbackRoute,
  googleVivatechRoute,
  shadcnPreviewRoute,
  demoKelloggLabsRoute,
  mediaInterviewRoute,
  networkCheckRoute,
  lmsLaunchRoute,
  lmsCheckRoute,
  // Standalone group-sims page is a development/test harness entry point only
  ...(import.meta.env.VITE_APP_ENV === "development" ? [multiSimDevRoute] : []),
])

export const router = new Router({
  routeTree,
  // Route chunks are lazy (code-splitting above); fetch a link's chunk the
  // moment the user hovers/focuses it so it's cached before the click and
  // navigation doesn't flash a loading state
  defaultPreload: "intent",
  // If a chunk still isn't ready (cold cache, slow network), hold the current
  // page for 300ms first; the fallback is deliberately blank — the app shell
  // stays visible and a spinner would double up with the boot overlay
  defaultPendingMs: 300,
  defaultPendingMinMs: 200,
})

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router
  }
  // Per-route metadata: the browser-tab title applied by <RouteTitleSync />
  // in Root. Dynamic pages override it at runtime via usePageMeta.
  interface StaticDataRouteOption {
    title?: string
  }
}

// Drill-through navigations (e.g. Analytics most-practiced lists → scenario
// detail) stash a back label in history state so the destination page can
// offer an in-app "Back" button returning to where the user came from,
// without needing new routes or search params. See <HistoryBackButton />.
declare module "@tanstack/history" {
  interface HistoryState {
    backLabel?: string
  }
}
